import 'photoswipe/style.css';
import 'swiper/css';
import '../sass/blocks/product-page.scss';
import { createProductGallery } from './utils/product-gallery.mjs';
import { createProductLightbox } from './utils/product-lightbox';
import { enhanceProductOptions } from './utils/product-options.mjs';
import { lockPageScroll } from './utils/scroll-lock';
import { enhanceCrossSellRail } from './utils/product-cross-sell.mjs';
import { initializeProductAddons } from './utils/product-addons.mjs';
import { initializeProductCoupon } from './utils/product-coupon.mjs';

const initialize = () => {
    const page = document.querySelector('[data-product-page]');
    const config = window.ovaliaProduct;
    if (!page || !config) return;
    const $ = window.jQuery;
    page.querySelectorAll('[data-product-cross-sell]').forEach(enhanceCrossSellRail);
    const gallery = createProductGallery(page.querySelector('[data-product-gallery]'), config);
    createProductLightbox(page, gallery, config);
    const form = page.querySelector('[data-product-purchase] form.cart');
    const price = page.querySelector('[data-product-price]');
    const availability = page.querySelector('[data-product-availability]');
    const feedback = page.querySelector('[data-product-feedback]');
    const initialPrice = price.innerHTML;
    const initialAvailability = availability.textContent;
    const syncCoupon = initializeProductCoupon(page);
    const syncSelections = () => {
        if (!form) return;
        const url = new URL(window.location.href);
        const selections = {};
        const labels = [];
        form.querySelectorAll('select[name^="attribute_"]').forEach((select) => {
            selections[select.name] = select.value;
            if (select.value) { url.searchParams.set(select.name, select.value); labels.push(select.selectedOptions[0].textContent); }
            else url.searchParams.delete(select.name);
        });
        if (window.location.href !== url.href) window.history.replaceState(window.history.state, '', url);
        page.querySelectorAll(`[data-duet-component="${page.dataset.productPage}"] [data-duet-variant]`).forEach((label) => { label.textContent = labels.join(' · '); });
        page.querySelectorAll('[data-set-preview]').forEach((trigger) => {
            trigger.dataset.setSelections = JSON.stringify({ [page.dataset.productPage]: selections });
        });
    };
    if (form && $) {
        const addons = initializeProductAddons(form, $, config, lockPageScroll);
        const options = enhanceProductOptions(form, config, syncSelections);
        if (options) page.classList.add('ProductPage--enhanced');
        $(form).on('found_variation.ovaliaProduct', (event, variation) => {
            price.innerHTML = variation.price_html || initialPrice;
            availability.innerHTML = variation.availability_html || config.available;
            gallery.showVariation(variation); syncSelections();
            syncCoupon(variation);
        }).on('reset_data.ovaliaProduct', () => {
            price.innerHTML = initialPrice;
            availability.textContent = initialAvailability;
            gallery.showVariation(null);
            syncCoupon();
        });
        // Covers variation resolution that happened before this script was ready.
        if (form.classList.contains('variations_form')) $(form).trigger('check_variations');
        syncSelections();
        let pending = false;
        let successTimer;
        const purchaseButton = form.querySelector('.single_add_to_cart_button');
        const originalButtonText = purchaseButton?.textContent;
        const resetButtonState = () => {
            clearTimeout(successTimer);
            if (!purchaseButton) return;
            delete purchaseButton.dataset.state;
            purchaseButton.removeAttribute('aria-busy');
            purchaseButton.textContent = originalButtonText;
        };
        form.addEventListener('change', () => { if (!pending) resetButtonState(); });
        form.addEventListener('submit', async (event) => {
            const button = purchaseButton;
            if (!button) return;
            const data = new FormData(form);
            if (!data.has('add-to-cart') && button.name === 'add-to-cart') data.set(button.name, button.value);
            // External products and third-party forms retain their native submit behavior.
            if (!data.has('add-to-cart')) return;
            event.preventDefault(); event.stopPropagation();
            if (pending) return;
            if (button.disabled || button.classList.contains('disabled')) {
                feedback.textContent = config.choose;
                form.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
                form.querySelector('.ProductOptions__input:not(:checked):not(:disabled)')?.focus();
                return;
            }
            data.set('action', 'verenza_add_to_cart');
            if (addons?.selected()) {
                if (!addons.validate()) { feedback.textContent = config.choose; return; }
                data.set('action', 'ovalia_add_bundle_to_cart');
                data.delete('add-to-cart');
            }
            resetButtonState();
            let added = false;
            pending = true; button.disabled = true; button.textContent = config.adding;
            button.dataset.state = 'loading';
            button.setAttribute('aria-busy', 'true');
            form.setAttribute('aria-busy', 'true');
            if (options) options.inert = true;
            addons?.lock(true);
            feedback.textContent = ''; delete feedback.dataset.error;
            try {
                const response = await fetch(config.ajaxUrl, { method: 'POST', credentials: 'same-origin', body: data });
                const result = await response.json();
                if (!response.ok || result.success === false) throw new Error(result.data?.message || config.error);
                if (result.error) {
                    feedback.innerHTML = result.error;
                    feedback.dataset.error = 'true';
                    return;
                }
                if (!result.fragments) throw new Error(config.error);
                Object.entries(result.fragments).forEach(([selector, html]) => { $(selector).replaceWith(html); });
                added = true;
                feedback.textContent = '';
                document.querySelector('#open-cart-btn')?.click();
            } catch (error) {
                feedback.textContent = error.name === 'Error' ? error.message : config.error; feedback.dataset.error = 'true';
            } finally {
                pending = false; button.disabled = false;
                button.removeAttribute('aria-busy');
                if (added) {
                    button.dataset.state = 'success';
                    button.textContent = config.addedButton;
                    successTimer = setTimeout(resetButtonState, 2200);
                } else resetButtonState();
                form.removeAttribute('aria-busy');
                if (options) options.inert = false;
                addons?.lock(false);
            }
        });
    }

    const dialogSupported = typeof HTMLDialogElement !== 'undefined' && HTMLDialogElement.prototype.showModal;
    let dialog;
    const showDialog = (title, content, trigger) => {
        if (!dialogSupported) return false;
        if (dialog?.open) dialog.close();
        dialog = document.createElement('dialog');
        dialog.className = 'ProductPageDialog';
        dialog.setAttribute('aria-label', title);
        const head = document.createElement('header'); head.className = 'ProductPageDialog__header';
        const heading = document.createElement('h2'); heading.textContent = title;
        const close = document.createElement('button'); close.type = 'button'; close.className = 'Button--no-style ProductPage__icon'; close.setAttribute('aria-label', config.close);
        const closeIcon = document.createElement('img'); Object.assign(closeIcon, { src: `${config.assets}header/close.svg`, width: 24, height: 24, alt: '' });
        close.append(closeIcon); head.append(heading, close); dialog.append(head, content); document.body.append(dialog);
        const current = dialog;
        const unlock = lockPageScroll();
        close.addEventListener('click', () => current.close());
        let outside = false;
        current.addEventListener('pointerdown', (event) => { outside = event.target === current && (event.offsetX < 0 || event.offsetY < 0 || event.offsetX > current.clientWidth || event.offsetY > current.clientHeight); });
        current.addEventListener('click', (event) => { if (outside && event.target === current) current.close(); });
        current.addEventListener('close', () => { unlock(); current.remove(); trigger.focus({ preventScroll: true }); }, { once: true });
        current.showModal(); close.focus();
        return true;
    };
    const guide = page.querySelector('[data-product-guide]');
    if (guide && form) {
        const purchaseControls = form.querySelector('.single_variation_wrap, .single_add_to_cart_button');
        purchaseControls?.before(guide);
    }
    if (guide && dialogSupported) guide.querySelector('summary').addEventListener('click', (event) => {
        event.preventDefault();
        showDialog(event.currentTarget.textContent, guide.querySelector('.ProductPage__prose').cloneNode(true), event.currentTarget);
    });
    const navigation = page.querySelector('.ProductPage__navigation');
    navigation?.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="#"]');
        const target = link && document.getElementById(link.hash.slice(1));
        if (!target || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        target.tabIndex = -1; target.focus({ preventScroll: true });
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        window.history.pushState(window.history.state, '', link.hash);
    });
    if (navigation && 'IntersectionObserver' in window) {
        const links = [...navigation.querySelectorAll('a')];
        const observer = new IntersectionObserver((entries) => {
            const visible = entries.find((entry) => entry.isIntersecting);
            if (!visible) return;
            links.forEach((link) => {
                if (link.hash === `#${visible.target.id}`) link.setAttribute('aria-current', 'location');
                else link.removeAttribute('aria-current');
            });
        }, { rootMargin: '-10% 0px -60% 0px', threshold: 0 });
        links.forEach((link) => { const section = document.getElementById(link.hash.slice(1)); if (section) observer.observe(section); });
    }
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
else initialize();
